import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiPost } from '../config/api';
import { ROUTES } from '../constants/routes';
import { markTutorialCompleted } from '../utils/tutorialStorage';

function isLocalDevHost() {
  if (process.env.NODE_ENV !== 'development') return false;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return host === 'localhost' || host === '127.0.0.1';
}

/**
 * In local dev, auto-sign-in as the seeded demo nurse so you can use the app
 * without hospital QR signup or manual login.
 */
export default function DevAutoLogin({ children }) {
  const { isAuthenticated, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(() => !isLocalDevHost() || isAuthenticated);

  useEffect(() => {
    if (!isLocalDevHost() || isAuthenticated) {
      setReady(true);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await apiPost('/api/dev/login', {});
        if (cancelled) return;
        loginWithToken(data.token, data.nurse?.name || 'Demo Nurse', {
          mustChangePassword: false,
          termsAccepted: data.terms_accepted ?? data.nurse?.terms_accepted,
        });
        markTutorialCompleted();
        navigate(ROUTES.PATIENTS, { replace: true });
      } catch (err) {
        console.warn('[DevAutoLogin] Could not auto-login:', err?.message || err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loginWithToken, navigate]);

  if (!ready && isLocalDevHost() && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-sm text-gray-500">
        Starting local demo…
      </div>
    );
  }

  return children;
}

export { isLocalDevHost };
